import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, History, Loader2, Save } from "lucide-react";
import { PromptWithVersions } from "@/types/prompt";
import { useOrganization } from "../../App";

export default function PromptEdit() {
  const { id } = useParams<{ id: string }>();
  const [prompt, setPrompt] = useState<PromptWithVersions | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    if (id) {
      fetchPrompt(id);
    }
  }, [id]);

  const fetchPrompt = async (promptId: string) => {
    try {
      setLoading(true);

      // Fetch the prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .select("*")
        .eq("id", promptId)
        .single();

      if (promptError) throw promptError;

      // Fetch the latest version
      const { data: versionData, error: versionError } = await supabase
        .from("prompt_versions")
        .select("*")
        .eq("prompt_id", promptId)
        .order("version_number", { ascending: false })
        .limit(1)
        .single();

      if (versionError) throw versionError;

      const promptWithVersion: PromptWithVersions = {
        ...promptData,
        versions: [versionData],
        latest_version: versionData,
      };

      setPrompt(promptWithVersion);
      setTitle(promptData.title);
      setDescription(promptData.description || "");
      setContent(versionData.content);
    } catch (error) {
      console.error("Error fetching prompt:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = async () => {
    if (!prompt) return;
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Please provide a title for your prompt.",
        variant: "destructive",
      });
      return;
    }

    if (!content.trim()) {
      toast({
        title: "Error",
        description: "Prompt content cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);

      // Update the prompt details
      const { error: promptError } = await supabase
        .from("prompts")
        .update({
          title,
          description: description || null,
          updated_at: new Date().toISOString(),
          organization_id: currentOrganization?.id || prompt.organization_id,
        })
        .eq("id", prompt.id);

      if (promptError) throw promptError;

      // Check if content has changed
      if (content !== prompt.latest_version?.content) {
        // Get the current user
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("User not authenticated");

        // Get the latest version number
        const { data: versionsData, error: versionsError } = await supabase
          .from("prompt_versions")
          .select("version_number")
          .eq("prompt_id", prompt.id)
          .order("version_number", { ascending: false })
          .limit(1);

        if (versionsError) throw versionsError;

        const nextVersionNumber =
          versionsData && versionsData.length > 0
            ? versionsData[0].version_number + 1
            : 1;

        // Create a new version
        const { error: versionError } = await supabase
          .from("prompt_versions")
          .insert({
            prompt_id: prompt.id,
            content,
            version_number: nextVersionNumber,
            created_by: user.id,
          });

        if (versionError) throw versionError;
      }

      toast({
        title: "Success",
        description: "Prompt updated successfully.",
      });

      // Navigate back to the prompt detail page
      navigate(`/prompt-engine/prompt/${prompt.id}`);
    } catch (error) {
      console.error("Error updating prompt:", error);
      toast({
        title: "Error",
        description: "Failed to update prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading prompt...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prompt) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Prompt not found.</p>
            <Button className="mt-4" onClick={() => navigate("/prompt-engine")}>
              Back to Prompts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">Edit Prompt</CardTitle>
            <CardDescription>
              Make changes to your prompt and save a new version.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/prompt-engine/history/${prompt.id}`)}
          >
            <History className="h-4 w-4 mr-1" /> View History
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">Prompt Title</Label>
            <Input
              id="title"
              placeholder="Enter a title for your prompt"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              placeholder="Enter a brief description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="content">Prompt Content</Label>
          <Textarea
            id="content"
            placeholder="Enter your prompt content"
            className="min-h-[300px] font-mono text-sm"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={saveChanges}
          disabled={!title.trim() || !content.trim()}
          isLoading={saving}
          loadingText="Saving..."
        >
          <Save className="mr-2 h-4 w-4" />
          Save Changes
        </Button>
      </CardFooter>
    </Card>
  );
}
